import { calculateAdjustments } from "../adjustments";
import { VariantCalculations } from "@/types/salary";
import { DEFAULT_LAW_YEAR } from "@/constants/salary";
import { laws } from "@/data-source";
import { calculateTax } from "../utils";
import { calculateNetSalary } from "../netSalary";
import { NetSalarySeekOptions } from "./types";

export class NetSalaryGoalSeek {
    seek(
        netSalary: number,
        {
            tolerance = 0.005,
            tolerancePercentage,
            ...options
        }: NetSalarySeekOptions
    ): [number, VariantCalculations] {
        if (!Number.isFinite(netSalary) || netSalary < 0)
            throw new RangeError("netSalary must be finite and nonnegative");
        if (!Number.isFinite(tolerance) || tolerance <= 0)
            throw new RangeError("tolerance must be finite and positive");
        if (tolerancePercentage !== undefined) {
            if (
                !Number.isFinite(tolerancePercentage) ||
                tolerancePercentage <= 0
            )
                throw new RangeError(
                    "tolerancePercentage must be finite and positive"
                );
            tolerance = Math.max(
                tolerance,
                (netSalary * tolerancePercentage) / 100
            );
        }

        const {
            bonus = [],
            deduction = [],
            insuranceWage,
            isInsured = true,
            lawYear = DEFAULT_LAW_YEAR,
        } = options;
        // Validate options through the same path used by the forward calculation.
        const { earnings: adjustment, cash } = calculateAdjustments(
            bonus,
            deduction
        );
        const minimumGross = Math.max(0, -adjustment, -adjustment - cash);
        calculateNetSalary({ ...options, grossSalary: minimumGross });
        const law = laws[lawYear];
        const earningsNet = netSalary - cash;
        const maximumGross = 2 ** 31 - 1;
        if (minimumGross > maximumGross)
            throw new RangeError(
                "Minimum gross salary exceeds the supported range"
            );
        const candidates = new Set<number>([minimumGross]);

        // Worker share = slope * adjusted gross + intercept in each insurance regime.
        const insuranceRegimes =
            !isInsured || insuranceWage !== undefined
                ? [
                      {
                          slope: 0,
                          intercept: isInsured
                              ? (insuranceWage ?? 0) * 0.11
                              : 0,
                      },
                  ]
                : [
                      { slope: 0, intercept: law.MinGrossSalary * 0.11 },
                      { slope: 0.11, intercept: 0 },
                      { slope: 0, intercept: law.MaxGrossSalary * 0.11 },
                  ];

        for (const bracket of law.brackets) {
            const taxSegments = [
                { rate: 0, intercept: 0 },
                ...bracket.stages.map((stage) => ({
                    rate: stage.percentage,
                    intercept:
                        calculateTax(bracket, stage.min) -
                        stage.min * stage.percentage,
                })),
            ];
            for (const worker of insuranceRegimes) {
                for (const tax of taxSegments) {
                    // Solve the continuous linear segment first. Rounding annual income
                    // down by <10 moves a root by <one annual ten-pound bucket for
                    // the supported rates (<=27.5%). Check its neighboring buckets.
                    const adjustedGross =
                        (earningsNet +
                            worker.intercept * (1 - tax.rate) -
                            (tax.rate * law.exemptionThreshold) / 12 +
                            tax.intercept / 12) /
                        ((1 - worker.slope) * (1 - tax.rate) - 0.0005);
                    const annualIncome =
                        12 *
                            (adjustedGross * (1 - worker.slope) -
                                worker.intercept) -
                        law.exemptionThreshold;
                    const bucket = Math.floor(annualIncome / 10) * 10;
                    for (const offset of [-10, 0, 10]) {
                        const annualTax = calculateTax(
                            bracket,
                            Math.max(0, bucket + offset)
                        );
                        const candidate =
                            (earningsNet + worker.intercept + annualTax / 12) /
                                (1 - worker.slope - 0.0005) -
                            adjustment;
                        if (
                            candidate >= minimumGross &&
                            candidate <= maximumGross
                        )
                            candidates.add(candidate);
                    }
                }
            }
        }

        // Tax-band changes and rounding can produce multiple solutions. Return the
        // lowest valid base gross; never return a candidate without checking it.
        for (const grossSalary of [...candidates].sort((a, b) => a - b)) {
            const [actualNet, calculations] = calculateNetSalary({
                ...options,
                grossSalary,
            });
            if (Math.abs(actualNet - netSalary) <= tolerance)
                return [grossSalary, calculations];
        }
        throw new RangeError(
            "No gross salary within the supported range produces the requested net salary"
        );
    }
}
